import {nextTick} from 'vue';
import axios from 'axios';
import _u from '../../shared/utils';
import {globalActions} from '../../store/global-actions';
import {formValidationMixin} from '../mixins/component-mixins';
import '../inputs/file-uploader';
import '../inputs/video-uploader';
import VueScrollTo from 'vue-scrollto';
import {configure, defineRule, Field, Form} from 'vee-validate';
// eslint-disable-next-line camelcase
import {between, one_of, regex} from '@vee-validate/rules';
import '../../shared/validation-rules/rules';
import localizedValidationMessages from '../../shared/validation-localization/localization';
import { store } from '../../store/global-store';
import { VideoUploadStatus, StoreCancellUploadingEventName } from '../../pages/wizard/video-uploading-status'

defineRule('between', between);
defineRule('regex', regex);
defineRule('oneOf', one_of);

function userIsUnauthorized(res) {
    return res.request.responseURL.includes('/account/login');
}

function userIsUnconfirmed(res) {
    return res && res.response && res.response.status === 403 &&
        typeof res.response.data === 'string' && res.response.data.includes('data-test-page="EmailNotConfirmed"');
}

const Wizard = {
    props: {
        isActive: {},
        model: {},
        currentSteps: {},
        initialStepId: {},
        basicStepId: {},
        loginStepId: {},
        authStepId: {},
        confirmEmailStepId: {},
        confirmEmailFallbackStepId: {},
        urls: {},
        afterPublishedStepId: {},
        labels: {},
        noTransition: {},
        publishAllowed: {},
        unauthorizedFallbackStepId: {},
        hiddenStepIds: {},
        publishIconPrefix: {},
    },
    mixins: [formValidationMixin],
    components: {
        Field: Field,
        ValidationForm: Form,
    },
    data() {
        return {
            isPreviewVisible: !this.isMobile(),
            isActionsVisible: true,
            stepLoadTimeout: 300,
            stepModel: this.model,
            steps: this.currentSteps,
            isDirtyModel: false,
            stepSubmitAction: null,
            saveBeforeSubmit: false,
            stepCancelAction: null,
            wizardInProgress: false,
            wizardStepInProgress: false,
            isStepModelVisible: this.model.visible,
            afterPublishStepId: this.afterPublishedStepId,
            preselectPublishStep: true,
            VideoUploadStatus,
            videoUploadStatus: VideoUploadStatus.CLOSED,
            uploadingVideoName: '',
            videoStepIsInit: false,
            isCancelUploadingModalOpened: false,
            isUnauthorizedModalOpened: false,
            showAuthorizationErrorDropdown: false,
        };
    },
    computed: {
        stepId() {
            if (this.stepModel == null) {
                return this.initialStepId;
            }

            return this.stepModel.stepId;
        },

        isNextStepAuthorization() {
            const currentStep = this.currentStep;
            const nextStep = this._getNextStep(currentStep)

            return nextStep.stepId === this.unauthorizedFallbackStepId;
        },

        isVideoProgressClosed() {
            return this.videoUploadStatus === VideoUploadStatus.CLOSED;
        },

        isVideoUploading() {
            return this.videoUploadStatus === VideoUploadStatus.UPLOADING
                || this.videoUploadStatus === VideoUploadStatus.CANCELLING;
        },

        currentStep() {
            let step = this.steps.find((step) => step.stepId === this.stepId);

            if (step.componentName === 'wizard-step-video') {
                this.videoStepIsInit = true;
            }

            return step;
        },

        modelId() {
            return this.stepModel ? this.stepModel.id : 0;
        },

        menuItemSteps() {
            return this.getMenuItemSteps();
        },

        isPublished() {
            return this.isStepModelVisible;
        },

        allRequiredPassed() {
            const authStep = this.steps.find((s) => s.stepId === this.loginStepId ||
                s.stepId === this.confirmEmailStepId);

            const areRequiredPassed = this.steps.filter((s) => s.isRequired).every((s) => s.isPassed);

            return this.modelId > 0 && !authStep && areRequiredPassed;
        },

        totalPoints() {
            return this.steps.map((step) => step.totalPoints).reduce((sum, points) => sum + points, 0);
        },

        scoredPoints() {
            return this.steps.map((step) => step.scoredPoints).reduce((sum, points) => sum + points, 0);
        },

        progressPercentsPassed() {
            return (this.scoredPoints / this.totalPoints) * 100;
        },

        isPreviewAvailable() {
            return this.allRequiredPassed && this.modelId > 0 && this.isWizardActionsVisible;
        },

        modelPreviewUrl() {
            return `${this.urls.previewUrl}/${this.modelId}`;
        },

        modelDetailsUrl() {
            return this.urls.detailsUrl.replace('/0', `/${this.modelId}`);
        },

        actionLabel() {
            if (this.currentStep.actionLabel) {
                return this.currentStep.actionLabel;
            }

            return this.isCurrentStepLast || this.isNextStepAuthorization ? this.labels.publish : this.labels.goNext;
        },

        secondaryActionLabel() {
            return this.stepCancelAction && this.currentStep.secondaryActionLabel ?
                this.currentStep.secondaryActionLabel : this.labels.goPrev;
        },

        hasPreviousStep() {
            return this.steps.includes(this.currentStep);
        },

        nextButtonDisabled() {
            const isInProgress = this.wizardInProgress || this.wizardStepInProgress;
            const isLastStep = this.actionLabel !== this.labels.goNext;

            return (isInProgress) || (isLastStep && this.isVideoUploading);
        },

        isCurrentStepLast() {
            return this.isLastStep(this.currentStep);
        },

        isPublishStep() {
            return this.currentStep.isPublishStep;
        },

        validationModel() {
            return this.stepModel;
        },

        isStepsVisible() {
            return this.allRequiredPassed && this.isWizardActionsVisible;
        },

        isProgressVisible() {
            return this.allRequiredPassed && this.isWizardActionsVisible;
        },

        isHideOrPublishAvailableMobile() {
            return this.allRequiredPassed && this.stepId !== this.unauthorizedFallbackStepId &&
                this.isWizardActionsVisible; // !this.isCurrentStepLast;
        },

        isPublishAvailableMobile() {
            return this.isHideOrPublishAvailableMobile && !this.isPublished &&
                this.isWizardActionsVisible && !this.isNextStepAuthorization &&
                this.actionLabel === this.labels.goNext;
        },

        isHideOrPublishAvailable() {
            return this.allRequiredPassed && this.stepId !== this.unauthorizedFallbackStepId && this.isWizardActionsVisible;
        },

        isNavigationVisible() {
            return this.isActionsVisible && !this.isHiddenStep || this.stepModel.showSecondaryNavigationButton;
        },

        isHiddenStep() {
            return this.hiddenStepIds && this.hiddenStepIds.includes(this.stepId);
        },

        isActionButtonVisible() {
            return !this.stepModel.forceHideActionButton;
        },

        hasMoreActions() {
            return this.isPublished;
        },

        showSummaryErrors() {
            return this.hasSummaryErrors && this.stepId !== this.unauthorizedFallbackStepId && !this.stepModel.handleValidationInStep;
        },

        isWizardActionsVisible() {
            return !this.stepModel.forceHideWizardActions;
        },
    },
    watch: {
        stepModel: {
            handler(newValue, oldValue) {
                if (newValue === oldValue) {
                    this.isDirtyModel = true;
                } else {
                    this.isDirtyModel = false;
                }
                if (newValue.afterPublishStepId) {
                    this.afterPublishStepId = newValue.afterPublishStepId;
                }
            },
            deep: true,
        },
        currentStep() {
            this._scrollToCurrentStep();
            this._removePendingSteps();
        },
    },
    methods: {
        stepModelInitialized() {
            this.$nextTick(() => {
                this.isDirtyModel = false;
            });
        },

        openAuthorizationErrorDropdown() {
            this.showAuthorizationErrorDropdown = true;
        },

        closeAuthorizationErrorDropdown() {
            this.showAuthorizationErrorDropdown = false;
        },

        async loadUnauthorizedFallbackStepAsync() {
          await this.loadStep(this.unauthorizedFallbackStepId);
        },

        tryCancellVideoUploading() {
            this.isCancelUploadingModalOpened = true;
        },

        cancellUploadingConfirmed() {
            this.cancelVideoUploading();
        },

        cancellUploadingCancelled() {
        },

        closeVideoUploadPopup() {
            this.videoUploadStatus = VideoUploadStatus.CLOSED;
        },

        startVideoUploading(fileName) {
            this.uploadingVideoName = fileName;
            this.videoUploadStatus = VideoUploadStatus.UPLOADING;
        },

        changeVideoUploadingStatus(status) {
            this.videoUploadStatus = status;
        },

        cancelVideoUploading() {
            store.dispatch(StoreCancellUploadingEventName);
        },

        approveCancel() {
            this.videoUploadStatus = VideoUploadStatus.CANCELLING;
        },

        continueUploading() {
            this.videoUploadStatus = VideoUploadStatus.UPLOADING;
        },

        getSteps() {
            if (this.steps) {
                return this.steps;
            }
            return this.currentSteps;
        },

        getVisibleSteps() {
            const steps = this.getSteps();
            if (this.hiddenStepIds) {
                return steps.filter((step) => !this.hiddenStepIds.includes(step.stepId));
            } else {
                return steps;
            }
        },

        getMenuItemSteps() {
            return this.getVisibleSteps();
        },

        getAfterPublishStep() {
            let afterPublishStepId = this.afterPublishStepId;
            if (this.stepModel && this.stepModel.afterPublishStepId) {
                afterPublishStepId = this.stepModel.afterPublishStepId;
            }

            return this.steps.find((step) => step.stepId === afterPublishStepId);
        },

        isCurrentStep(step) {
            return step.stepId === this.stepId;
        },

        getRemainingPoints(step) {
            return step.totalPoints - step.scoredPoints;
        },

        getRemainingPointsLabel(step) {
            const pointsCount = this.getRemainingPoints(step);
            return `+ ${pointsCount} ${this.getPointsLabel(pointsCount)}`;
        },

        getPointsLabel(pointsCount) {
            return _u.getLabelByCount(pointsCount,
                this.labels.pointSingle,
                this.labels.pointsTwo,
                this.labels.pointsMultiple);
        },

        isPrevPassed(step) {
            const prevStep = this._getPreviousStep(step);

            if (prevStep && prevStep.scoredPoints > 0) {
                return true;
            }

            return false;
        },

        isNextPassed(step) {
            const nextStep = this._getNextStep(step);

            if (nextStep && nextStep.scoredPoints > 0) {
                return true;
            }

            return false;
        },

        isLastStep(step) {
            const steps = this.getVisibleSteps();
            const index = steps.findIndex((s) => s.stepId === step.stepId);
            return index === steps.length - 1;
        },

        isFirstStep(step) {
            const index = this.steps.indexOf(step);
            return index === 0;
        },

        async publishOrHideAsync() {
            if (this.isPublished) {
                this._hideListing();
            } else {
                this.publishAllowed ? await this.saveAndPublishAsync() : await this.loadLastStepAsync();
            }
        },

        async forcePublishAsync() {
            if (this.stepId !== this.confirmEmailFallbackStepId) {
                await this.saveAndPublishAsync(true);
            }
        },

        async goNext() {
            this._clearErrors();
            const needSave = this.isDirtyModel || this.modelId === 0 || !this.allRequiredPassed;

            if (this.stepSubmitAction) {
                if (this.saveBeforeSubmit && needSave) {
                    await this._saveAndSubmitAsync();
                } else {
                    this.stepSubmitAction();
                }
                return;
            }

            if (needSave) {
                try {
                    const response = await this._saveStep();
                    this._handleSaveResult(response);
                } catch (err) {
                    await this._handleErrorsAsync(err);
                } finally {
                    this._progressStopped();
                }
            } else {
                await this.loadNextStepAsync();
            }
        },

        async _saveAndSubmitAsync() {
            try {
                const response = await this._saveStep();
                this._handleSaveResult(response);

                await this.$nextTick(() => {
                    this.stepSubmitAction();
                });
            } catch (err) {
                await this._handleErrorsAsync(err);
            } finally {
                this._progressStopped();
            }
        },

        async goPrev() {
            this._clearErrors();

            if (this.stepCancelAction) {
                this.stepCancelAction();
                return;
            }

            let previousStep = this._getPreviousStep(this.currentStep);

            if (previousStep.stepId === this.authStepId) {
                const loginStep = this.steps.find((s) => s.stepId === this.authStepId);
                previousStep = this._getPreviousStep(loginStep);
                this.removeStep(this.authStepId);
                this.hiddenStepIds.push(this.authStepId);
            }

            if (previousStep) {
                await this.loadStep(previousStep.stepId);
            } else {
                window.history.back();
            }
        },

        async saveAndPublishAsync(force = false) {
            if (this.isDirtyModel || force || this.currentStep.isPublishStep || this.isCurrentStepLast) {
                this._prepareModel();
                try {
                    const response = await this._saveStep();

                    if (!force && !this.isCurrentStepLast) {
                        this._handleSaveResult(response);
                        this.changeLastRequiredStepLabel();
                    }
                    await this._publish(response, force);
                } catch (errorResult) {
                    await this._handleErrorsAsync(errorResult);
                } finally {
                    this._progressStopped();
                }
            } else {
                await this.loadNextStepAsync();
            }
        },

        changeLastRequiredStepLabel() {
            this._getLastRequiredStepId().actionLabel = this.labels.goNext;
        },

        _getLastRequiredStepId() {
            const requiredSteps = this.steps.filter((s) => s.isRequired);
            return requiredSteps[requiredSteps.length - 1];
        },

        switchPreviewVisibility() {
            this.isPreviewVisible = !this.isPreviewVisible;
        },

        async tryLoadStepAsync(stepId) {
            if (this.unauthorizedFallbackStepId === stepId) {
                this.isUnauthorizedModalOpened = true;
            }
            else {
                await this.loadStep(stepId);
            }
        },

        async loadStep(stepId) {
            if (this.stepId === stepId) {
                return;
            }

            if (this.isDirtyModel || this.wizardStepInProgress === true) {
                this.$refs.confirmLeaveStepModal.open(() => {
                    this.wizardStepInProgress = false;
                    this._startLoadStepAsync(stepId);
                });
            } else {
                await this._startLoadStepAsync(stepId);
            }
        },

        removeStep(stepId) {
            if (stepId !== this.stepId) {
                const indexToRemove = this.steps.findIndex((s) => s.stepId === stepId);
                this.steps.splice(indexToRemove, 1);
            }
        },

        hideActions() {
            this.isActionsVisible = false;
        },

        closeVideoProgressBar() {
            this.videoUploadStatus = VideoUploadStatus.CLOSED;
        },

        _removePendingSteps() {
            // Can not remove the step until the next step loaded as it's used in vue component
            if (this.stepIdToRemove && this.stepIdToRemove !== this.stepId) {
                const indexToRemove = this.steps.findIndex((s) => s.stepId === this.stepIdToRemove);
                this.steps.splice(indexToRemove, 1);
                this.stepIdToRemove = null;
            }
        },

        async _startLoadStepAsync(stepId) {
            this._clearErrors();
            this._progressStarted();

            const url = `${this.urls.loadStepUrl}?stepId=${stepId}&modelId=${this.modelId}`;

            try {
                const response = await axios.get(url, {headers: {}});

                if (response.status === 200 && response.data) {
                    this.stepModel = response.data;
                    await this.$nextTick(() => {
                        this.isDirtyModel = false;
                    });
                }
            } catch (err) {
                await this._handleErrorsAsync(err);
            } finally {
                this._progressStopped();
            }
        },

        async stepLoaded(stepData) {
            this.stepSubmitAction = stepData.submitAction;
            this.saveBeforeSubmit = stepData.saveBeforeSubmit;
            this.stepCancelAction = stepData.cancelAction;

            if (!this.stepSubmitAction && (this.isCurrentStepLast || this.isPublishStep)) {
                if (this.preselectPublishStep) {
                    this.stepSubmitAction = await this.saveAndPublishAsync;
                }
            }

            this._scrollToStepContentStart();
            await this._scrollToCurrentStep();
        },

        _getPreviousStep(step) {
            if (!this.steps || this.steps.length === 0) {
                return null;
            }

            const currentIndex = this.steps.indexOf(step);
            let prevStep = null;

            if (currentIndex > 0) {
                prevStep = this.steps[currentIndex - 1];
            }

            return prevStep;
        },

        _getNextStep(step) {
            if (!this.steps || this.steps.length === 0) {
                return null;
            }

            const currentIndex = this.steps.indexOf(step);
            let nextStep = null;

            if (currentIndex >= 0 && currentIndex + 1 < this.steps.length) {
                nextStep = this.steps[currentIndex + 1];
            }

            return nextStep;
        },

        async loadNextStepAsync() {
            const nextStep = this._getNextStep(this.currentStep) || this.steps[0];

            if (nextStep) {
                await this.tryLoadStepAsync(nextStep.stepId);
            }
        },

        async loadLastStepAsync() {
            const lastStep = this.steps[this.steps.length - 1];
            await this.tryLoadStepAsync(lastStep.stepId);
        },

        async _saveStep() {
            if (this.stepModel) {
                this._progressStarted();
                const url = `${this.urls.saveStepUrl}?stepId=${this.stepId}&modelId=${this.modelId}`;

                return await axios.post(url, this.stepModel);
            }
        },

        async validateFieldsAsync() {
            const isValid = await this._validateAsync();
            if (isValid) {
                await this.goNext();
            } else {
                this._scrollToValidationErrors();
            }
        },

        async _validateAsync() {
            const validator = this.$refs.validator;
            const result = await validator.validate();

            return result.valid;
        },

        _handleSubmitError() { },

        _hideListing() {
            if (this.isPublished && this.modelId > 0) {
                this.$refs.confirmHideListing.open(() => {
                    const url = `${this.urls.hideUrl}/${this.modelId}`;
                    this._progressStarted();

                    axios.post(url)
                        .then((response) => {
                            if (response && response.data === false) {
                                this.isStepModelVisible = false;
                                this._reloadPreview();
                            } else {
                                globalActions.handleAjaxErrors(response);
                            }
                        })
                        .catch(this._handleErrorsAsync)
                        .finally(this._progressStopped);
                });
            }
        },

        async _publish(response, force = false, shouldSkipHandleResult = false) {
            this._clearErrors();

            this._progressStarted();
            const url = `${this.urls.publishUrl}/${this.modelId}`;

            try {
                const response = await axios.post(url);

                if (!shouldSkipHandleResult) {
                    await this._handlePublishResultAsync(response, force);
                }
            } catch (errorResult) {
                await this._handleErrorsAsync(errorResult);
            } finally {
                this._progressStopped();
            }
        },

        async _handlePublishResultAsync(response, force = false) {
            if (userIsUnauthorized(response)) {
                this.isUnauthorizedModalOpened = true;
            } else {
                if (response && response.data && response.data.redirectUrl) {
                    this.isDirtyModel = false;

                    if (this.isCurrentStepLast || force) {
                        const afterPublishStep = this.getAfterPublishStep();
                        await this._startLoadStepAsync(afterPublishStep.stepId);
                        this.closeVideoProgressBar();

                    } else {
                        this.isStepModelVisible = true;
                        await this._reloadStepIfVisibilityChangedAsync();
                        this._reloadPreview();
                    }
                } else {
                    globalActions.handleAjaxErrors(response);
                }
            }
        },

        _handleSaveResult(response) {
            if (!response || !response.data) {
                console.error('Save result is empty.');
                globalActions.handleAjaxErrors(response);
                return;
            }

            const saveResult = response.data;

            if (saveResult.steps) {
                this.steps = saveResult.steps;
            }

            if (saveResult.nextStepModel.stepId === this.unauthorizedFallbackStepId) {
                this.stepModel.id = saveResult.nextStepModel.id;
                this.$nextTick(() => {
                    this.isDirtyModel = false;
                });

                this.isUnauthorizedModalOpened = true;
            }
            else {
                this.stepModel = saveResult.nextStepModel;

                this.$nextTick(() => {
                    this.isDirtyModel = false;
                });

                this._reloadPreview();
            }
        },

        _reloadPreview() {
            const previewComponent = this.$refs['previewComponent'];

            if (previewComponent) {
                previewComponent.reload();
            }
        },

        async _reloadStepIfVisibilityChangedAsync() {
            if (this.stepModel.visible !== this.isStepModelVisible) {
                await this._startLoadStepAsync(this.stepId);
            }
        },

        _scrollToCurrentStep() {
            nextTick(() => {
                const stepRef = this.$refs[`step-${this.stepId}`];
                if (stepRef) {
                    const stepEl = stepRef[0].$el;
                    setTimeout(() => {
                        stepEl.scrollIntoView({block: 'center', inline: 'center', behavior: 'smooth'});
                    }, 125);
                }
            });
        },

        _scrollToStepContentStart() {
            const formEl = this.$refs.wizardStepForm;
            if (formEl) {
                formEl.scrollIntoView({block: 'start', inline: 'start'});
            }
        },

        async _handleErrorsAsync(errorResult) {
            if (userIsUnconfirmed(errorResult)) {
                await this.loadStep(this.confirmEmailFallbackStepId);
            } else {
                this._handleValidationErrors(errorResult);
                this._scrollToValidationErrors();

                return Promise.resolve();
            }
        },

        async _checkStepInUrlAsync() {
            const hashPrefix = '#wizard-step-';
            const isWizardProperHash = window.location.hash.startsWith(hashPrefix);

            if (window.location.hash && isWizardProperHash) {
                const stepName = window.location.hash.substr(1);
                const step = this.steps.find((s) => s.componentName === this.getComponentName(stepName));

                if (this._isHistoryNavigationAvailable(step) && this.stepId !== step.stepId) {
                    await this.tryLoadStepAsync(step.stepId);
                }
            } else if (!isWizardProperHash) {
                window.location.hash = '';
            }
        },

        getComponentName(defaultComponentName) {
            return defaultComponentName ? defaultComponentName : null;
        },

        _isHistoryNavigationAvailable(step) {
            return step && (this.allRequiredPassed || step.stepId < this.stepId);
        },

        _subscribeWindowUnload() {
            window.onbeforeunload = () => {
                return (this.isDirtyModel && this.modelId > 0)
                    || this.videoUploadStatus === VideoUploadStatus.UPLOADING ? '' : null;
            };
        },

        _progressStarted() {
            this.wizardInProgress = true;
        },

        _progressStopped() {
            this.wizardInProgress = false;
        },

        isMobile() {
            return window.innerWidth < 768;
        },

        _prepareModel() {
        },

        showFeedbackButton() {
            if (this.isMobile()) {
                globalActions.showFeedbackButton();
            }
        },

        _subscribeDocumentEvents() {
            document.onmouseover = () => {
                window.innerClick = true;
            };

            document.onmouseleave = () => {
                window.innerClick = false;
            };

            window.addEventListener('popstate', () => {
                window.innerClick = false;
            });
        },

        getPublishIconId() {
            return `${this.publishIconPrefix}{_u.genUid()}`;
        },

        async _subscribeLoginEventAsync() {
            store.subscribe(async (mutation) => {
                if (mutation.type === 'loginAdvertWizard') {
                    const publishUrl = `${this.urls.publishUrl}/${this.modelId}`;

                    try {
                        const response = await axios.post(publishUrl);

                        if (response && response.status === 200 && response.data.redirectUrl) {
                            window.location.href = response.data.redirectUrl;
                        }
                    } catch (errorResult) {
                        await this._handleErrorsAsync(errorResult);
                    } finally {
                        this._progressStopped();
                    }
                }
            });
        },

        _subscribeRegisterEvent() {
            store.subscribe(async (mutation) => {
                if (mutation.type === 'registerAdvertWizard') {
                    this.preselectPublishStep = false;
                    this.wizardStepInProgress = false;
                    await this.loadNextStepAsync();
                    this.removeStep(this.authStepId);
                }
            });
        },
    },
    async mounted() {
        await this.$nextTick(async () => {
            await this._checkStepInUrlAsync();
        });

        this._subscribeWindowUnload();
        this._subscribeDocumentEvents();

        await this._subscribeLoginEventAsync();
        this._subscribeRegisterEvent();

        window.onhashchange = async () => {
            await this._checkStepInUrlAsync();
        };
    },
};

const wizardStepMixin = {
    props: {
        modelValue: {},
        title: {},
        inProgress: {},
        summaryErrors: {},
    },
    components: {
        Field: Field,
        ValidationForm: Form,
    },
    data() {
        return {
            model: this.modelValue,
            submitAction: null,
            cancelAction: null,
            stepInProgress: this.inProgress,
        };
    },
    watch: {
        stepInProgress(val) {
            this.$emit('update:in-progress', val);
        },

        inProgress(val) {
            this.stepInProgress = val;
        },
    },
    computed: {
        isInitialStepPassed() {
            return this.model.id > 0;
        },
    },
    methods: {
        stepLoaded() {
            this.$emit('step-loaded', {
                stepId: this.modelValue.stepId,
                submitAction: this.submitAction,
                saveBeforeSubmit: this.saveBeforeSubmit,
                cancelAction: this.cancelAction,
            });
        },

        initComponent() {
            this.stepLoaded();

            const hash = window.location.hash;
            if (window.innerClick || !hash) {
                window.history.pushState(window.location.state, document.title, this.$options.template);
                if (window.ga) {
                    window.ga('send', 'pageview', { 'page': location.pathname + location.search + location.hash });
                }
            }
        },

        _progressStarted() {
            this.stepInProgress = true;
        },
        _progressStopped() {
            this.stepInProgress = false;
        },
    },
    mounted() {
        this.initComponent();
    },
    activated() {
        this.model = this.modelValue;
        this.stepLoaded();
    },
};

function initWizardComponents(app) {
    app.use(VueScrollTo);

    configure({
        generateMessage: localizedValidationMessages(),
    });

    app.component('wizard', {
        extends: Wizard,
        template: '#wizard-template',
    });
}

export {
    initWizardComponents,
    Wizard,
    wizardStepMixin,
};
